import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight, Users, GraduationCap, Sparkles, Shield, HandHeart,
  BookOpen, MessageSquare, HeartPulse, Compass, CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";
import SectionHeader from "@/components/site/SectionHeader";
import { submitContact } from "@/lib/api";

const AUDIENCES = [
  {
    title: "Organisations & professionals",
    text: "Interactive sexual health and wellbeing workshops for organisations, community services, and professionals working alongside young people.",
    tags: ["Community services", "Youth workers", "Health providers", "NGOs"],
  },
  {
    title: "Schools & educators",
    text: "Professional education and workshops for teachers, school staff, wellbeing teams, pastoral care staff and other professionals supporting students.",
    tags: ["Teachers", "Wellbeing teams", "Pastoral care", "School leadership"],
  },
  {
    title: "Rangatahi",
    text: "Evidence-informed, age-appropriate and culturally responsive sexual health and wellbeing education that supports rangatahi to build knowledge, confidence and skills.",
    tags: ["Sexual health literacy", "Consent", "Healthy relationships", "Wellbeing"],
  },
];

const TOPICS = [
  { icon: HeartPulse, title: "Sexual health & wellbeing" },
  { icon: BookOpen, title: "Sexual health literacy" },
  { icon: HandHeart, title: "Healthy relationships" },
  { icon: Shield, title: "Consent" },
  { icon: MessageSquare, title: "Communication" },
  { icon: Users, title: "Supporting rangatahi with sexual health conversations" },
  { icon: HeartPulse, title: "Reproductive & sexual health" },
  { icon: Sparkles, title: "Inclusive sexual health education" },
  { icon: Compass, title: "Navigating sexual health information" },
  { icon: MessageSquare, title: "Accessing appropriate sexual health services" },
  { icon: Compass, title: "Referral pathways" },
  { icon: GraduationCap, title: "Building professional confidence" },
];

const WHY = [
  "Evidence-informed & Hauora-aligned",
  "Culturally responsive & Te Tiriti grounded",
  "Inclusive of LGBTQIA+, takatāpui & rainbow rangatahi",
  "Tailored to the setting, audience and needs of each partner",
];

export default function Workshops() {
  const [form, setForm] = useState({
    name: "", email: "", organisation: "", enquirer_type: "",
    topic: "", audience_size: "", message: "",
  });
  const [loading, setLoading] = useState(false);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const bodyParts = [
        form.enquirer_type && `Enquiring on behalf of: ${form.enquirer_type}`,
        form.topic && `Preferred topic(s): ${form.topic}`,
        form.audience_size && `Audience size: ${form.audience_size}`,
        form.message,
      ].filter(Boolean);
      await submitContact({
        name: form.name,
        email: form.email,
        organisation: form.organisation,
        subject: "Workshop enquiry",
        message: bodyParts.join("\n\n"),
      });
      toast.success("Enquiry sent — we'll be in touch shortly.");
      setForm({ name: "", email: "", organisation: "", enquirer_type: "", topic: "", audience_size: "", message: "" });
    } catch {
      toast.error("Couldn't send enquiry. Please try again.");
    } finally { setLoading(false); }
  };

  return (
    <>
      {/* HERO */}
      <section className="container-x pt-20 pb-12 sm:pt-28">
        <div className="max-w-4xl">
          <span className="label-eyebrow">— Workshops &amp; Professional Education</span>
          <h1 className="mt-5 text-4xl sm:text-5xl lg:text-7xl font-heading font-semibold tracking-tighter leading-[1.02] text-balance">
            Tailored sexual health and wellbeing workshops.
          </h1>
          <p className="mt-8 text-lg text-ink/65 max-w-2xl leading-relaxed">
            Aoturoa Academy delivers practical, evidence-informed workshops for organisations, schools,
            professionals — and directly for rangatahi. Every workshop is shaped to the audience, setting and
            kaupapa of the people we're working alongside.
          </p>
          <div className="mt-9 flex flex-wrap gap-3">
            <a href="#enquire" data-testid="workshops-enquire-top" className="btn-crimson">Enquire About a Workshop <ArrowUpRight className="w-4 h-4" /></a>
            <a href="#topics" data-testid="workshops-topics-jump" className="btn-outline">See workshop topics</a>
          </div>
        </div>
      </section>

      {/* WHO WE WORK WITH */}
      <section className="section-y !pt-12">
        <div className="container-x">
          <SectionHeader eyebrow="Who we work with" title="Workshops built around who you support." />
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
            {AUDIENCES.map((a) => (
              <article key={a.title} className="card-surface flex flex-col" data-testid={`workshop-audience-${a.title.split(" ")[0].toLowerCase()}`}>
                <h3 className="text-xl font-heading font-semibold text-ink leading-snug">{a.title}</h3>
                <p className="mt-3 text-ink/65 leading-relaxed flex-1">{a.text}</p>
                <div className="mt-6 pt-5 border-t border-stone flex flex-wrap gap-2">
                  {a.tags.map((t) => (
                    <span key={t} className="text-[11px] uppercase tracking-wider font-semibold text-crimson bg-crimson/5 border border-crimson/15 px-2.5 py-1 rounded-full">{t}</span>
                  ))}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* TOPICS */}
      <section id="topics" className="bg-sand">
        <div className="container-x py-20 lg:py-28">
          <SectionHeader
            eyebrow="Workshop topics"
            title="What we can cover — shaped to your setting."
            description="Not every workshop covers every topic. Workshop content is tailored to the needs, setting and audience of each organisation or school. Below is the breadth of what we can bring."
          />
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {TOPICS.map(({ icon: Icon, title }) => (
              <div key={title} className="bg-white border border-stone rounded-2xl p-5 flex items-center gap-4 hover:border-ink/30 transition-all">
                <div className="w-10 h-10 rounded-lg bg-sand flex items-center justify-center flex-shrink-0">
                  <Icon className="w-4 h-4 text-crimson" />
                </div>
                <span className="text-sm text-ink/85 leading-snug">{title}</span>
              </div>
            ))}
          </div>
          <p className="mt-8 text-sm text-ink/55 italic">
            Aoturoa Academy provides education and professional development — we do not diagnose, treat, or provide clinical healthcare services.
          </p>
        </div>
      </section>

      {/* WHY / HOW */}
      <section className="section-y">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-5">
            <span className="label-eyebrow">— Our approach</span>
            <h2 className="mt-4 text-3xl sm:text-4xl font-heading font-semibold leading-tight text-balance">
              Practical, evidence-informed, always tailored.
            </h2>
          </div>
          <ul className="lg:col-span-7 space-y-4">
            {WHY.map((w) => (
              <li key={w} className="flex items-start gap-3 bg-white border border-stone rounded-2xl px-6 py-5">
                <CheckCircle2 className="w-5 h-5 text-crimson flex-shrink-0 mt-0.5" />
                <span className="text-ink/85 text-[15px]">{w}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ENQUIRY */}
      <section id="enquire" className="bg-ink text-bone">
        <div className="container-x py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <span className="label-eyebrow">— Enquire About a Workshop</span>
              <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-heading font-semibold tracking-tight text-balance">
                Let's shape something for your team.
              </h2>
              <p className="mt-6 text-bone/70 leading-relaxed">
                Tell us a little about your school, organisation, or team and what you'd like to explore. We respond to
                every enquiry personally, usually within 2–3 business days.
              </p>
            </div>
            <form onSubmit={onSubmit} className="lg:col-span-7 bg-white text-ink rounded-3xl p-8 sm:p-10 border border-stone" data-testid="workshop-enquiry-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="text-sm font-medium text-ink/80 mb-2 block">Your name</label>
                  <input required className="input-field" value={form.name} onChange={onChange("name")} data-testid="workshop-name" />
                </div>
                <div>
                  <label className="text-sm font-medium text-ink/80 mb-2 block">Email</label>
                  <input required type="email" className="input-field" value={form.email} onChange={onChange("email")} data-testid="workshop-email" />
                </div>
                <div>
                  <label className="text-sm font-medium text-ink/80 mb-2 block">School / organisation</label>
                  <input className="input-field" value={form.organisation} onChange={onChange("organisation")} data-testid="workshop-organisation" />
                </div>
                <div>
                  <label className="text-sm font-medium text-ink/80 mb-2 block">Enquiring on behalf of</label>
                  <select className="input-field" value={form.enquirer_type} onChange={onChange("enquirer_type")} data-testid="workshop-enquirer-type">
                    <option value="">Select...</option>
                    <option>A school</option>
                    <option>An organisation</option>
                    <option>A community service</option>
                    <option>A professional team</option>
                    <option>Another group</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-ink/80 mb-2 block">Preferred topic(s)</label>
                  <input className="input-field" placeholder="e.g. consent, healthy relationships" value={form.topic} onChange={onChange("topic")} data-testid="workshop-topic" />
                </div>
                <div>
                  <label className="text-sm font-medium text-ink/80 mb-2 block">Approx. audience size</label>
                  <input className="input-field" placeholder="e.g. 25 staff, 60 students" value={form.audience_size} onChange={onChange("audience_size")} data-testid="workshop-audience-size" />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-sm font-medium text-ink/80 mb-2 block">Tell us more</label>
                  <textarea required rows={5} className="input-field resize-none" value={form.message} onChange={onChange("message")} data-testid="workshop-message" />
                </div>
              </div>
              <button type="submit" disabled={loading} className="mt-7 btn-primary" data-testid="workshop-submit">
                {loading ? "Sending..." : <>Send enquiry <ArrowUpRight className="w-4 h-4" /></>}
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* PROGRAMME CROSS-LINK */}
      <section className="section-y !pt-16">
        <div className="container-x">
          <div className="bg-sand rounded-3xl p-8 sm:p-12 grid grid-cols-1 md:grid-cols-3 gap-8 items-center">
            <div className="md:col-span-2">
              <span className="label-eyebrow">— Working directly with rangatahi</span>
              <h2 className="mt-4 text-2xl sm:text-3xl font-heading font-semibold leading-snug">
                Our rangatahi programme complements our workshops for professionals.
              </h2>
              <p className="mt-4 text-ink/65 leading-relaxed">
                Alongside professional workshops, Aoturoa Academy continues to deliver evidence-informed sexual health
                and wellbeing education directly to rangatahi.
              </p>
            </div>
            <Link to="/programmes" data-testid="workshops-programme-link" className="btn-outline w-full md:w-auto justify-self-end">
              Our Programme <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
